/**
 * Company Delivery Provider
 * Abstraction for delivering signed contract PDFs to company-scoped storage.
 *
 * v1: same Supabase project, company-scoped private bucket.
 * Designed so that a future v2 adapter (external company Supabase) can be
 * substituted without changing the submission flow.
 */

import { supabase } from '@xanectech/supabase-client';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('CompanyDeliveryProvider');

export interface CompanyDeliveryParams {
  submissionId: string;
  companyId: string;
  pdfBlob: Blob;
  fileName: string;
}

export interface CompanyDeliveryResult {
  storagePath: string;
}

/**
 * Provider interface — implement to support alternative delivery targets
 * (e.g. a company's own Supabase project).
 */
export interface ICompanyDeliveryProvider {
  deliverSignedContract(
    params: CompanyDeliveryParams,
  ): Promise<CompanyDeliveryResult>;
}

/**
 * v1 implementation: stores the signed PDF in the same Supabase project under a
 * company-scoped path in the 'company-assets' private bucket.
 *
 * Storage path: {companyId}/{submissionId}/{fileName}
 *
 * Prerequisites:
 *  - A private storage bucket named 'company-assets' must exist.
 *  - RLS policies should allow company admins to read objects whose path starts
 *    with their company_id.
 */
export class SameProjectCompanyDeliveryProvider implements ICompanyDeliveryProvider {
  async deliverSignedContract({
    submissionId,
    companyId,
    pdfBlob,
    fileName,
  }: CompanyDeliveryParams): Promise<CompanyDeliveryResult> {
    const storagePath = `${companyId}/${submissionId}/${fileName}`;

    logger.info('Delivering signed contract to company storage', {
      companyId,
      submissionId,
      storagePath,
    });

    const { error } = await supabase.storage
      .from('company-assets')
      .upload(storagePath, pdfBlob, {
        contentType: 'application/pdf',
        upsert: true,
      });

    if (error) {
      logger.error('Failed to deliver signed contract to company storage', {
        companyId,
        submissionId,
        error: error.message,
      });
      throw error;
    }

    logger.info('Signed contract delivered to company storage', {
      companyId,
      submissionId,
      storagePath,
    });

    return { storagePath };
  }
}
